import { useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { format } from 'date-fns'
import fileDownload from 'js-file-download'

import { PageTitle } from '../../../../_metronic/layout/core'
import { convertToUTCByMSK, convertUTCtoMSK, formatSum } from '../../../../utils'
import { STATUS_COLOR } from '../../../../constants'
import { Table } from '../../../../components/Table'
import { TableHead } from '../../../../components/Table/TableHead'
import { TableBody } from '../../../../components/Table/TableBody'
import { FilterManager } from '../../../common/filter/FilterManager'
import { PayoutTokenLink } from '../../../common/PayoutTokenLink'
import { ReportBlock } from '../../payment-tickets/components/ReportBlock'
import { sortReportBlocks } from '../../payment-tickets/core/_consts'
import { ROLE, useAuth } from '../../auth'
import { IsGranted } from '../../auth/PrivateRoute'
import {
  exportMatchingPayoutReport,
  getMatchingPayoutDetailed,
  getMatchingPayoutSummary,
  IMatchingPayoutReportParams,
} from '../api/getMatchingPayoutReport'
import { IMatchingPayoutReportRow, IMatchingPayoutSummaryRow } from '../ReportsPage.types'
import {
  MATCHING_PAYOUT_BREADCRUMBS,
  MATCHING_PAYOUT_REPORT_ROLES,
  REPORT_OUTCOME_OPTIONS,
} from '../core/consts'
import {
  IMatchingPayoutFilters,
  MATCHING_PAYOUT_DATE_MODE,
  MATCHING_PAYOUT_DATE_MODES,
} from '../core/matchingPayout.types'
import {
  getMatchingPayoutDefaultFilters,
  MATCHING_PAYOUT_PER_PAGE,
} from '../core/matchingPayout.defaults'
import { useMatchingPayoutFilterOptions } from '../core/useMatchingPayoutFilterOptions'
import { ClientCell } from '../../../common/ClientCell'

/**
 * Отчёт по выплатам мэтчинга (CPAYMENT-1760).
 *
 * Строка таблицы — один депозитный чанк, а не выплата целиком: сумма строки принадлежит мерчанту
 * ДЕПОЗИТА. Поэтому у одной выплаты может быть несколько строк, а `ticketsCount` в карточках
 * сводки считает выплаты и намеренно не совпадает с числом строк.
 */
const MatchingPayoutReportPage = () => {
  const { currentUser } = useAuth()
  const canSeeReport = IsGranted(MATCHING_PAYOUT_REPORT_ROLES, currentUser)
  /**
   * Селект адаптера открыт менеджеру мерчантов наравне с сотрудниками платёжки — он и разбирает
   * отчёты в разрезе PSP. Шире гейт не идёт: справочник `/api/internal/payment-adapter/lookup`
   * доступен только ROLE_MANAGER и ROLE_MERCHANT_MANAGER, у остальных ролей страницы селект был бы
   * пустым. Тот же список стоит на паре отчётов в `ReportTicketsFilter`.
   */
  const canFilterByAdapter = IsGranted(
    [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER],
    currentUser
  )

  const defaultFilters = useMemo(() => getMatchingPayoutDefaultFilters(), [])
  const [filters, setFilters] = useState<IMatchingPayoutFilters>(defaultFilters)
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<IMatchingPayoutReportRow[]>([])
  const [blocks, setBlocks] = useState<IMatchingPayoutSummaryRow[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const { merchantOptions, adapterOptions, paymentMethodOptions } =
    useMatchingPayoutFilterOptions(canFilterByAdapter)

  /**
   * Тело без пагинации — его ждут сводка и выгрузка. `page` / `perPage` объявлены только в
   * теле детализации, и лишнее поле парсер бэкенда отвергает целиком.
   */
  const buildQuery = (source: IMatchingPayoutFilters): IMatchingPayoutReportParams => {
    const from = source.dateRange?.from
    const to = source.dateRange?.to

    return {
      // Обе границы конвертируем из МСК только здесь, в payload; в состоянии остаётся Date.
      dateStart: convertToUTCByMSK(from instanceof Date ? from : new Date(from as any)),
      dateEnd: convertToUTCByMSK(to instanceof Date ? to : new Date(to as any)),
      merchantTokenList: source.merchantTokenList ?? [],
      paymentAdapterIdList: (source.paymentAdapterIdList ?? []).map(Number),
      paymentMethods: source.paymentMethods ?? [],
      outcomes: source.outcomes ?? [],
      byCompletionDate: source.dateRange?.mode === MATCHING_PAYOUT_DATE_MODE.completed,
    }
  }

  const load = async (source: IMatchingPayoutFilters, targetPage: number) => {
    setIsLoading(true)

    const query = buildQuery(source)

    const [summaryResponse, detailedResponse] = await Promise.all([
      getMatchingPayoutSummary(query),
      getMatchingPayoutDetailed({ ...query, page: targetPage, perPage: MATCHING_PAYOUT_PER_PAGE }),
    ])

    setIsLoading(false)

    if (summaryResponse.data?.success && summaryResponse.data.data) {
      setBlocks(summaryResponse.data.data)
    }

    if (detailedResponse.data?.success && detailedResponse.data.data) {
      setRows(detailedResponse.data.data)
      setTotalRows(detailedResponse.data.totalRows)
    }
  }

  useEffect(() => {
    if (canSeeReport) {
      load(defaultFilters, 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeReport])

  const handleApplyFilters = (applied: IMatchingPayoutFilters) => {
    setFilters(applied)
    setPage(1)
    load(applied, 1)
  }

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(filters, selected)
  }

  const handleExport = async () => {
    // Выгрузка идёт без пагинации — в этом режиме бэкенд отдаёт всю выборку и даты в МСК.
    const query = buildQuery(filters)
    const { data } = await exportMatchingPayoutReport(query)

    if (data instanceof Blob) {
      fileDownload(
        data,
        `matching-payout-report-${format(
          new Date(query.dateStart),
          'dd-MM-yyyy_HH-mm'
        )}__${format(new Date(query.dateEnd), 'dd-MM-yyyy_HH-mm')}.xlsx`
      )
    }
  }

  const totalTicketsCount = blocks.reduce(
    (total: number, statusData: IMatchingPayoutSummaryRow) => total + statusData.ticketsCount,
    0
  )

  const blocksRender = sortReportBlocks(blocks).map((statusData: IMatchingPayoutSummaryRow) => (
    <div className='col-xl-4' key={statusData.status}>
      <ReportBlock
        className='card-xl-stretch mb-xl-8'
        color={STATUS_COLOR[statusData.status]}
        status={statusData.status}
        sum={formatSum(statusData.sum)}
        count={statusData.ticketsCount}
        total={totalTicketsCount}
        commission={statusData.status === 'COMPLETED' ? statusData.commission_sum : null}
      />
    </div>
  ))

  if (!canSeeReport) {
    return <div>Роль юзера не имеет соответствующих прав для просмотра этого отчёта.</div>
  }

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={MATCHING_PAYOUT_BREADCRUMBS}>Отчёт</PageTitle>
        <div className='card-body border-0 pt-5 pb-2'>
          <h3 className='card-title fw-bold fs-3 mb-2 d-block'>Отчёт по выплатам мэтчинга</h3>
          <div className='text-muted fw-semibold fs-7'>
            Объём депозитов, ушедших в выплаты мэтчинга. Строка — один депозит, сумма принадлежит
            мерчанту депозита, а не владельцу выплаты.
          </div>
        </div>
      </div>

      <FilterManager<IMatchingPayoutFilters>
        activeFilters={[
          'dateRange',
          { filter: 'selectMulti', fieldName: 'merchantTokenList' },
          {
            filter: 'selectMulti',
            fieldName: 'paymentAdapterIdList',
            visibility: canFilterByAdapter,
          },
          { filter: 'selectMulti', fieldName: 'paymentMethods' },
          { filter: 'selectMulti', fieldName: 'outcomes' },
        ]}
        defaultValues={defaultFilters}
        filterProps={{
          dateRange: {
            label: 'Период',
            // Ось периода — режим самого dateRange, отдельного селекта под неё нет.
            modes: MATCHING_PAYOUT_DATE_MODES,
            ranges: ['currentShift', 'prevShift', '24hours', '7days', '30days'],
          },
          merchantTokenList: {
            label: 'Мерчант депозита',
            options: merchantOptions,
            placeholder: 'Все',
          },
          paymentAdapterIdList: {
            label: 'Платёжный адаптер',
            options: adapterOptions,
            placeholder: 'Все',
          },
          paymentMethods: {
            label: 'Платёжный метод',
            options: paymentMethodOptions,
            placeholder: 'Все',
          },
          outcomes: {
            label: 'Статус выплаты',
            options: REPORT_OUTCOME_OPTIONS,
            placeholder: 'Все',
          },
        }}
        onApplyFilters={handleApplyFilters}
        actions={
          <button className='btn btn-primary' onClick={handleExport}>
            Экспортировать
          </button>
        }
      />

      <div className='card mb-7'>
        <div className='row g-5 g-xl-8 pt-5 ps-5'>{blocksRender}</div>
      </div>

      <div className='card'>
        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th className='min-w-150px'>Тикет выплаты</th>
                <th className='min-w-150px'>Депозит</th>
                <th className='min-w-140px'>Клиент</th>
                <th className='min-w-120px'>Мерчант депозита</th>
                <th className='min-w-120px'>Контрагент</th>
                <th className='min-w-140px'>Дата создания</th>
                <th className='min-w-140px'>Дата закрытия</th>
                <th className='min-w-120px'>Сумма депозита</th>
                <th className='min-w-140px'>Платежный метод</th>
                <th className='min-w-120px'>ID транзакции</th>
                <th className='min-w-120px'>Статус выплаты</th>
              </tr>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <tr>
                  <td colSpan={11}>Идёт загрузка...</td>
                </tr>
              ) : rows.length ? (
                rows.map((row: IMatchingPayoutReportRow) => (
                  <tr key={`${row.token}-${row.depositToken}`}>
                    <td>
                      <PayoutTokenLink token={row.token} />
                    </td>
                    <td>{row.depositToken}</td>
                    <ClientCell externalId={row.externalUserId} token={row.clientToken} />
                    <td>{row.merchant ?? '-'}</td>
                    <td>
                      {row.counterpartyMerchant ?? (
                        <span className='text-muted' title='Мерчант выплаты вне вашей области'>
                          скрыт
                        </span>
                      )}
                    </td>
                    <td>{row.createdDate ? convertUTCtoMSK(row.createdDate) : '-'}</td>
                    <td>{row.completedDate ? convertUTCtoMSK(row.completedDate) : '-'}</td>
                    <td>{formatSum(row.amount)}</td>
                    <td>{row.paymentMethod ?? '-'}</td>
                    <td>{row.platformTransactionId ?? '-'}</td>
                    <td>{row.status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11}>Нет данных за выбранный период</td>
                </tr>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ReactPaginate
        activeClassName={'active-pagination '}
        breakClassName={'item-pagination break-me-pagination '}
        containerClassName={'pagination-pagination'}
        disabledClassName={'disabled-page-pagination'}
        nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
        pageClassName={'item-pagination pagination-page-pagination '}
        previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
        breakLabel='...'
        nextLabel=''
        onPageChange={(pageNumber) => handlePageChange(pageNumber.selected + 1)}
        forcePage={page - 1}
        pageRangeDisplayed={5}
        pageCount={Math.ceil(totalRows / MATCHING_PAYOUT_PER_PAGE) || 1}
        previousLabel=''
        renderOnZeroPageCount={null}
      />
    </div>
  )
}

export default MatchingPayoutReportPage
